import { useCallback, useMemo, useRef } from "react";
import { getBeatsPerMeasure, getSecondsPerBeat } from "../utils/timing";

export function useBeatClock({ bpm, beatsPerMeasure }) {
  const anchorAtRef = useRef(null);
  const anchorBeatRef = useRef(0);

  const secondsPerBeat = useMemo(() => getSecondsPerBeat(bpm), [bpm]);
  const beatsPerMeasureSafe = useMemo(() => getBeatsPerMeasure(beatsPerMeasure), [beatsPerMeasure]);

  const setAnchor = useCallback(
    (anchorAt, anchorBeat = 0) => {
      if (!Number.isFinite(secondsPerBeat) || secondsPerBeat <= 0) return false;
      if (!Number.isFinite(anchorAt)) return false;
      if (!Number.isFinite(anchorAtRef.current)) {
        anchorAtRef.current = anchorAt;
        anchorBeatRef.current = Number.isFinite(anchorBeat) ? anchorBeat : 0;
        return true;
      }
      return false;
    },
    [secondsPerBeat],
  );

  const resetAnchor = useCallback(() => {
    anchorAtRef.current = null;
    anchorBeatRef.current = 0;
  }, []);

  const beatToTime = useCallback(
    (beatIndex) => {
      if (!Number.isFinite(secondsPerBeat) || secondsPerBeat <= 0) return null;
      if (!Number.isFinite(anchorAtRef.current)) return null;
      if (!Number.isFinite(beatIndex)) return null;
      return anchorAtRef.current + (beatIndex - anchorBeatRef.current) * secondsPerBeat;
    },
    [secondsPerBeat],
  );

  const timeToBeat = useCallback(
    (audioTime) => {
      if (!Number.isFinite(secondsPerBeat) || secondsPerBeat <= 0) return null;
      if (!Number.isFinite(anchorAtRef.current)) return null;
      if (!Number.isFinite(audioTime)) return null;
      return anchorBeatRef.current + (audioTime - anchorAtRef.current) / secondsPerBeat;
    },
    [secondsPerBeat],
  );

  const isAnchored = useCallback(() => Number.isFinite(anchorAtRef.current), []);

  return useMemo(
    () => ({
      secondsPerBeat,
      beatsPerMeasure: beatsPerMeasureSafe,
      anchorAtRef,
      anchorBeatRef,
      setAnchor,
      resetAnchor,
      beatToTime,
      timeToBeat,
      isAnchored,
    }),
    [beatToTime, beatsPerMeasureSafe, isAnchored, resetAnchor, secondsPerBeat, setAnchor, timeToBeat],
  );
}

export default useBeatClock;
